import { Base } from '../../abstract/base.model';
import { isRecord } from '../utils/is-record';
import { dateOrNull, nullableBoolean, nullableNumber, nullableString } from '../utils/wire-value-utils';
import { BillingResponsibility } from './billing-responsibility.model';
import { BusinessType } from './business-type.model';
import { OrganizationType } from './organization-type.model';

/**
 * An organization, as the hub's `toOrganizationResponse` returns it (`user-organizations-controller.ts`).
 * Mirrors hub `data-models/organization.ts`.
 *
 * Read-modify-write rule: writable fields the hub didn't send stay `undefined` (an explicit `null` stays
 * `null`). `ein` / `ssn` are only sent to the org's owner, so they are `undefined` for everyone else.
 * Display fallbacks belong in templates.
 */
export class Organization extends Base {
  // Writable fields (hub ORGANIZATION_WRITABLE)
  name?: string | null;
  organizationType?: OrganizationType | null;
  ein?: string | null;
  ssn?: string | null;
  streetAddressLine1?: string | null;
  streetAddressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  country?: string | null;
  email?: string | null;
  phone?: string | null;
  serviceRadiusMiles?: number | null;

  // Read-only fields
  businessType?: BusinessType | null;
  ownerProfileId?: string | null;
  chiefSupervisorProfileId?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  parentOrganizationId?: string | null;
  billingResponsibility?: BillingResponsibility | null;
  setupComplete?: boolean | null;
  archivedAt: Date | null;
  createdBy?: string | null;
  updatedBy?: string | null;

  constructor(data: unknown) {
    super(data);
    const source = isRecord(data) ? data : {};
    this.archivedAt = dateOrNull(source['archivedAt']);
    this.readWritableFields(source);
    this.readHubFields(source);
  }

  private readWritableFields(source: Record<string, unknown>): void {
    this.name = nullableString(source['name']);
    this.organizationType = nullableString(source['organizationType']) as OrganizationType | null | undefined;
    this.ein = nullableString(source['ein']);
    this.ssn = nullableString(source['ssn']);
    this.streetAddressLine1 = nullableString(source['streetAddressLine1']);
    this.streetAddressLine2 = nullableString(source['streetAddressLine2']);
    this.city = nullableString(source['city']);
    this.state = nullableString(source['state']);
    this.zipCode = nullableString(source['zipCode']);
    this.country = nullableString(source['country']);
    this.email = nullableString(source['email']);
    this.phone = nullableString(source['phone']);
    this.serviceRadiusMiles = nullableNumber(source['serviceRadiusMiles']);
  }

  private readHubFields(source: Record<string, unknown>): void {
    this.businessType = nullableString(source['businessType']) as BusinessType | null | undefined;
    this.ownerProfileId = nullableString(source['ownerProfileId']);
    this.chiefSupervisorProfileId = nullableString(source['chiefSupervisorProfileId']);
    this.latitude = nullableNumber(source['latitude']);
    this.longitude = nullableNumber(source['longitude']);
    this.parentOrganizationId = nullableString(source['parentOrganizationId']);
    this.billingResponsibility = nullableString(source['billingResponsibility']) as BillingResponsibility | null | undefined;
    this.setupComplete = nullableBoolean(source['setupComplete']);
    this.createdBy = nullableString(source['createdBy']);
    this.updatedBy = nullableString(source['updatedBy']);
  }
}
